import { DictionaryLookupDS } from "@/dataSources/DictionaryLookupDS";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import { TEMPLATE_EXTENSIONS } from "@/helper/globals";
import { useDocumentTemplateAdd } from "@/hooks/useDocumentTemplateAdd";
import { Dictionary } from "@/types/types";
import {
  Button,
  CheckBox,
  FileUploader,
  LoadIndicator,
  Lookup,
  Popup,
  TextArea,
  TextBox,
  ValidationGroup,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { PopupRef } from "devextreme-react/cjs/popup";
import { ValidationGroupRef } from "devextreme-react/cjs/validation-group";
import { RequiredRule } from "devextreme-react/validator";
import { ClickEvent } from "devextreme/ui/button";
import { ValueChangedEvent } from "devextreme/ui/file_uploader";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

type Props = {
  datagridRef: React.RefObject<DataGridRef>;
  popupRef: React.RefObject<PopupRef>;
};

export default function AddDocumentTemplate({ datagridRef, popupRef }: Props) {
  const { mutate: addTemplate, isPending, isSuccess, data } = useDocumentTemplateAdd();
  const validationRef = useRef<ValidationGroupRef>(null);

  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [type, setType] = useState<Dictionary>();
  const [file, setFile] = useState<string>();
  const [fileName, setFileName] = useState("");
  const [isDefault, setIsDefault] = useState<boolean | null>(true);

  function submit(e: ClickEvent) {
    if (!e.validationGroup.validate().isValid) return;

    addTemplate({
      Name: name,
      FileName: fileName,
      Comment: comment,
      TemplateTypeID: type?.id,
      FileValue: file,
      IsDefault: !!isDefault,
    });
  }

  useEffect(() => {
    if (isSuccess && !data?.validation?.items?.length) {
      toast.success("Succesfully added");
      closePopup(popupRef);
    }
  }, [data?.validation?.items?.length, isSuccess, popupRef]);

  function onFileChanged(e: ValueChangedEvent) {
    if (!e.value) return;
    const blob = e.value[0];

    if (!blob) return;

    setFileName(blob?.name);

    const fr = new FileReader();

    fr.readAsDataURL(blob);

    fr.onload = () => {
      if (fr.result && typeof fr.result === "string") {
        setFile(fr.result);
      }
    };
  }

  function resetFields() {
    setName("");
    setComment("");
    setType(undefined);
    setIsDefault(false);
  }

  return (
    <Popup
      title="Add dictionary entry"
      showCloseButton={true}
      ref={popupRef}
      onHiding={() => {
        resetFields();
        validationRef.current?.instance()?.reset();
        reloadGrid(datagridRef);
      }}
    >
      <ValidationGroup ref={validationRef}>
        <form className="mx-1 grid grid-cols-2 gap-8">
          <TextBox
            value={name}
            onValueChange={(value) => setName(value)}
            label="Name"
            labelMode="static"
            stylingMode="outlined"
            valueChangeEvent="keyup"
          >
            <Validator>
              <RequiredRule message={"Name is required"} />
            </Validator>
          </TextBox>
          <Lookup
            value={type}
            onValueChange={(value) => setType(value)}
            label="Type"
            displayExpr="name"
            dataSource={DictionaryLookupDS("90")}
            stylingMode="outlined"
            labelMode="static"
            searchEnabled={true}
            showClearButton={true}
          >
            <Validator>
              <RequiredRule message={"Type is required"} />
            </Validator>
          </Lookup>
          <TextArea
            value={comment}
            onValueChange={(value) => setComment(value)}
            label="Comment"
            stylingMode="outlined"
            labelMode="static"
            valueChangeEvent="keyup"
          />
          <FileUploader
            uploadMode={"useForm"}
            allowedFileExtensions={TEMPLATE_EXTENSIONS}
            onValueChanged={onFileChanged}
          >
            <Validator>
              <RequiredRule message={"File is required"} />
            </Validator>
          </FileUploader>
          <div className="col-start-2 mt-4">
            <CheckBox
              value={!!isDefault}
              onValueChange={(value) => setIsDefault(value)}
              text="Default"
            />
          </div>
        </form>
        <div className="absolute bottom-4 left-4">
          <ValidationSummary id="summary"></ValidationSummary>
        </div>
        <div className="absolute bottom-4 right-4">
          <Button
            useSubmitBehavior={false}
            type="default"
            height={40}
            width={isPending ? 150 : 100}
            onClick={submit}
          >
            <LoadIndicator
              id="default-btn-loading"
              className="mr-3 inline-block align-middle"
              width={25}
              height={25}
              visible={isPending}
            />
            <span className="dx-button-text">{isPending ? "Inserting" : "Insert"}</span>
          </Button>
        </div>
      </ValidationGroup>
    </Popup>
  );
}
